import { isBrowser } from "./Browser";

export const THEME_PREFERENCE_STORAGE_KEY = "themePreference";
export const THEME_CHANGE_EVENT = "themePreferenceChanged";

export const THEME_PREFERENCE_LIGHT = "light";
export const THEME_PREFERENCE_DARK = "dark";
export const THEME_PREFERENCE_SYSTEM = "system";

const VALID_THEME_PREFERENCES = new Set([
  THEME_PREFERENCE_LIGHT,
  THEME_PREFERENCE_DARK,
  THEME_PREFERENCE_SYSTEM,
]);

export const isValidThemePreference = (value) => {
  return VALID_THEME_PREFERENCES.has(value);
};

export const getSystemTheme = () => {
  if (!isBrowser() || typeof window.matchMedia !== "function") {
    return THEME_PREFERENCE_LIGHT;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? THEME_PREFERENCE_DARK
    : THEME_PREFERENCE_LIGHT;
};

export const getThemePreference = () => {
  if (!isBrowser()) {
    return THEME_PREFERENCE_SYSTEM;
  }
  const storedThemePreference = localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
  if (isValidThemePreference(storedThemePreference)) {
    return storedThemePreference;
  }
  return THEME_PREFERENCE_SYSTEM;
};

export const resolveTheme = (themePreference = getThemePreference()) => {
  if (themePreference === THEME_PREFERENCE_SYSTEM) {
    return getSystemTheme();
  }
  if (isValidThemePreference(themePreference)) {
    return themePreference;
  }
  return THEME_PREFERENCE_LIGHT;
};

export const applyTheme = (themePreference = getThemePreference()) => {
  if (!isBrowser() || typeof document === "undefined") {
    return THEME_PREFERENCE_LIGHT;
  }
  const resolvedTheme = resolveTheme(themePreference);
  const rootElement = document.documentElement;
  rootElement.setAttribute("data-theme", resolvedTheme);
  rootElement.style.colorScheme = resolvedTheme;
  return resolvedTheme;
};

const createThemeChangedEvent = (themePreference, resolvedTheme) => {
  if (typeof window.CustomEvent === "function") {
    return new CustomEvent(THEME_CHANGE_EVENT, {
      detail: {
        preference: themePreference,
        theme: resolvedTheme,
      },
    });
  }
  const event = document.createEvent("CustomEvent");
  event.initCustomEvent(
    THEME_CHANGE_EVENT,
    false,
    false,
    { preference: themePreference, theme: resolvedTheme }
  );
  return event;
};

export const setThemePreference = (themePreference) => {
  if (!isBrowser()) {
    return;
  }
  const safeThemePreference = isValidThemePreference(themePreference)
    ? themePreference
    : THEME_PREFERENCE_SYSTEM;
  localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, safeThemePreference);
  const resolvedTheme = applyTheme(safeThemePreference);

  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(createThemeChangedEvent(safeThemePreference, resolvedTheme));
  }
};
